import { useEffect, useState, Fragment } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty } from "../components/Layout";
import MarginLadder from "../components/MarginLadder";
import WinLossChart from "../components/WinLossChart";
import ReplyAnalytics from "../components/ReplyAnalytics";
import { Trophy, IndianRupee, CheckCircle2, Clock, Sparkles } from "lucide-react";

const METRICS = [
  { key: "calls", label: "Calls", t: "calls" },
  { key: "meetings", label: "Meetings", t: "meetings" },
  { key: "replies", label: "Replies", t: "replies" },
  { key: "deals", label: "Deals", t: "deals" },
];

function Cell({ value, target }) {
  const pct = target ? Math.min(100, (value / target) * 100) : 0;
  const hit = target && value >= target;
  return (
    <td className="px-3 py-2">
      <div className="flex items-center gap-2">
        <span className={`font-mono tabular-nums text-sm ${hit ? "text-[#2F7A3E]" : "text-[#1C2530]"}`}>{value}</span>
        {target ? <span className="font-mono text-[10px] text-[#1C2530]/40">/ {target}</span> : null}
      </div>
      {target ? <div className="h-1 bg-[#F5F3EE] border border-[#DFDAD0] mt-1"><div className="h-full" style={{ width: `${pct}%`, background: hit ? "#2F7A3E" : "#3E8F99" }} /></div> : null}
    </td>
  );
}

export default function Productivity() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [coach, setCoach] = useState(null);
  const isLead = user.role === "supervisor";
  useEffect(() => { api.get("/productivity").then((r) => setRows(r.data)); }, []);

  const getCoaching = async (r) => {
    setCoach({ id: r.id, loading: true });
    try {
      const list = await api.get(`/targets?rep_id=${r.id}`);
      const confirmed = list.data.find((t) => t.status === "confirmed");
      if (!confirmed) { setCoach({ id: r.id, text: "No confirmed targets yet — set & get sign-off first." }); return; }
      const { data } = await api.get(`/targets/${confirmed.id}/coaching`);
      setCoach({ id: r.id, text: data.coaching });
    } catch (e) { setCoach({ id: r.id, text: e.response?.data?.detail || "Failed" }); }
  };

  return (
    <>
      <PageHeader eyebrow="TARGETS VS ACHIEVEMENT · TEAM LEADERBOARD" title="Productivity Dashboard" hint="Team leaderboard: each rep's activity and outcomes against their targets for the current period. Targets are set in Target Setting." />
      <div className="p-8">
        {(user.role === "admin" || user.role === "supervisor") && <><MarginLadder /><WinLossChart /></>}
        <ReplyAnalytics />
        {!rows ? <Loading /> : rows.length === 0 ? <Empty /> : (
          <div className="border border-[#DFDAD0] bg-white" data-testid="productivity-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                <th className="text-left px-3 py-2.5 w-10">#</th>
                <th className="text-left px-3 py-2.5">Rep</th>
                {METRICS.map((m) => <th key={m.key} className="text-left px-3 py-2.5">{m.label}</th>)}
                <th className="text-left px-3 py-2.5">Revenue (₹)</th>
                <th className="text-left px-3 py-2.5">Margin</th>
                <th className="text-left px-3 py-2.5">Sign-off</th>
                {isLead && <th className="text-right px-3 py-2.5">Coaching</th>}
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <Fragment key={r.id}>
                    <tr data-testid={`prod-row-${r.id}`} className={`border-t border-[#DFDAD0] ${r.id === user.id ? "bg-[#3E8F99]/5" : i % 2 ? "bg-[#F5F3EE]/40" : ""}`}>
                      <td className="px-3 py-2 font-mono text-xs">{i === 0 ? <Trophy className="w-4 h-4 text-[#E2711D]" /> : i + 1}</td>
                      <td className="px-3 py-2 font-medium text-[#1C2530]">{r.rep}<span className="font-mono text-[10px] text-[#1C2530]/40 ml-1">{r.region}</span></td>
                      {METRICS.map((m) => <Cell key={m.key} value={r[m.key]} target={r.targets?.[m.t]} />)}
                      <td className="px-3 py-2" data-testid={`revenue-cell-${r.id}`}>
                        <span className={`font-mono tabular-nums text-sm ${r.revenue_target && r.revenue >= r.revenue_target ? "text-[#2F7A3E]" : "text-[#1C2530]"}`}>₹{(r.revenue || 0).toLocaleString("en-IN")}</span>
                        {r.revenue_target ? <span className="font-mono text-[10px] text-[#1C2530]/40"> / ₹{r.revenue_target.toLocaleString("en-IN")}</span> : null}
                      </td>
                      <td className="px-3 py-2" data-testid={`margin-cell-${r.id}`}>
                        <span className={`font-mono tabular-nums text-sm ${r.avg_margin_pct == null ? "text-[#1C2530]/50" : r.avg_margin_pct >= 40 ? "text-[#2F7A3E]" : r.avg_margin_pct >= 20 ? "text-[#8a6a12]" : "text-[#B23A2E]"}`}>{r.avg_margin_pct == null ? "—" : `${r.avg_margin_pct}%`}</span>
                        {r.proposals ? <span className="font-mono text-[10px] text-[#1C2530]/40"> · {r.proposals} prop.</span> : null}
                      </td>
                      <td className="px-3 py-2" data-testid={`signoff-${r.id}`}>
                        {r.signoff === "confirmed" ? <span className="flex items-center gap-1 font-mono text-[10px] uppercase text-[#2F7A3E]"><CheckCircle2 className="w-3.5 h-3.5" /> Confirmed</span>
                          : r.signoff === "pending" ? <span className="flex items-center gap-1 font-mono text-[10px] uppercase text-[#E2711D]"><Clock className="w-3.5 h-3.5" /> Pending</span>
                          : <span className="font-mono text-[10px] uppercase text-[#1C2530]/30">None</span>}
                      </td>
                      {isLead && <td className="px-3 py-2 text-right"><button onClick={() => getCoaching(r)} data-testid={`coach-btn-${r.id}`} className="flex items-center gap-1 border border-[#7A5CB2] text-[#7A5CB2] px-2 py-1 font-mono text-[9px] uppercase hover:bg-[#7A5CB2]/10 ml-auto"><Sparkles className="w-3 h-3" /> Coach</button></td>}
                    </tr>
                    {coach && coach.id === r.id && (
                      <tr data-testid={`coach-row-${r.id}`}><td colSpan={isLead ? 10 : 9} className="px-4 py-3 bg-[#7A5CB2]/5 border-t border-[#DFDAD0]">
                        <div className="font-mono text-[9px] uppercase tracking-wider text-[#7A5CB2] mb-1">Coaching suggestion · Sales Lead only</div>
                        <div className="text-sm text-[#1C2530]">{coach.loading ? "Analyzing…" : coach.text}</div>
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
